import { useEffect, useMemo, useState } from 'react'

import useGetActivityWorkloadLevel from '~/bootcamp/hooks/useGetActivityWorkloadLevel'
import useGetAvailableRewards from '~/bootcamp/hooks/useGetAvailableRewards'
import useGetCollectedRewards from '~/bootcamp/hooks/useGetCollectedRewards'
import useGetCurrentDifficultyLevel from '~/bootcamp/hooks/useGetCurrentDifficultyLevel'
import useGetGemType from '~/bootcamp/hooks/useGetGemType'
import { useGetRewardsInChest } from '~/bootcamp/hooks/useGetRewardsInChest'
import {
  selectBootcampActivityEvents,
  selectCorrectConsecutiveAnswers,
  selectCurrentActivityType,
  selectShouldGetEnergyOrbs,
} from '~/bootcamp/slices/bootcamp-activity.slice'
import { selectBootcampPageActivities } from '~/bootcamp/slices/bootcamp-page.slice'
import { selectVccComplexityLevel } from '~/bootcamp/slices/vcc-data.slice'
import { BootcampActivitySet } from '~/bootcamp/types/bootcamp.types'
import { getXPCountInChest } from '~/bootcamp/utils/bootcamp.utils'
import { getChestcontentSize } from '~/components/Chest/helpers'
import { useAppSelector } from '~/redux/hooks'
import AnimationGems from '~/shared/components/RewardsChest/AnimationGems'
import AnimationOrbs from '~/shared/components/RewardsChest/AnimationOrbs'
import AnimationXp from '~/shared/components/RewardsChest/AnimationXp'

export default function Rewards({
  isLarge = false,
  isDelay = false,
}: {
  isLarge?: boolean
  isDelay?: boolean
}): React.ReactElement {
  const events = useAppSelector(selectBootcampActivityEvents)
  const activities = useAppSelector(selectBootcampPageActivities) || []
  const bootcampComplexityLevel = useAppSelector(selectVccComplexityLevel) || 1
  const activityWorkloadLevel = useGetActivityWorkloadLevel(activities)
  const [difficultyLevel] = useGetCurrentDifficultyLevel(events, activities)
  const gemType = useGetGemType(activities)

  const correctConsecutiveAnswers = useAppSelector(selectCorrectConsecutiveAnswers)
  const energyOrbs = useAppSelector(selectShouldGetEnergyOrbs)
  const hasEnergyOrb = correctConsecutiveAnswers === 3 ? energyOrbs : false

  const collectedRewards = useGetCollectedRewards(events)
  const availableRewards = useGetAvailableRewards(activities)

  const activityType = useAppSelector(selectCurrentActivityType)
  const currentActivitySet = activities.find((elem) => elem.type === activityType)

  const isAtLastDifficultyLevel = collectedRewards + 1 === availableRewards

  const rewardsForChest = useGetRewardsInChest(
    currentActivitySet as unknown as BootcampActivitySet,
    bootcampComplexityLevel,
  )

  const rewardsCount = useMemo(() => {
    if (isAtLastDifficultyLevel) {
      return rewardsForChest
    }

    return {
      gems: 0,
      xp: Math.round(getXPCountInChest(bootcampComplexityLevel, activityWorkloadLevel, difficultyLevel) * 0.75),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityWorkloadLevel, bootcampComplexityLevel, difficultyLevel, isAtLastDifficultyLevel])

  const [showReward, setShowReward] = useState(false)

  useEffect(() => {
    let delay: NodeJS.Timeout
    if (isDelay) {
      delay = setTimeout(() => {
        setShowReward(true)
      }, 350)
    } else {
      setShowReward(true)
    }
    return () => {
      clearTimeout(delay)
    }
  }, [isDelay])

  const className = isLarge ? `relative z-10 ${getChestcontentSize('large')}` : ''
  return (
    <>
      {showReward && (
        <div className={className}>
          <AnimationGems gemType={gemType} noOfGems={rewardsCount.gems} />
          <AnimationXp noOfXPs={rewardsCount.xp} />
          {hasEnergyOrb && <AnimationOrbs />}
        </div>
      )}
    </>
  )
}
